import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money, fmtKST, getToken, ITEM_TYPES, itemPhotoUrl } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Bits";
import { StatusTimeline } from "@/components/StatusTimeline";

const Stepper = ({ value, max, onChange, testId }) => (
  <div className="flex items-center gap-1" data-testid={testId}>
    <button type="button" aria-label="Less" disabled={value <= 0} onClick={() => onChange(value - 1)} className="h-8 w-8 grid place-items-center rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-30 transition-colors"><Minus className="h-3.5 w-3.5" /></button>
    <span data-testid={`${testId}-value`} className="w-8 text-center font-mono font-semibold">{value}</span>
    <button type="button" aria-label="More" disabled={value >= max} onClick={() => onChange(value + 1)} className="h-8 w-8 grid place-items-center rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-30 transition-colors"><Plus className="h-3.5 w-3.5" /></button>
  </div>
);

const ItemRow = ({ it, sdId, qty, setQty, canClaim, onZoom }) => (
  <div data-testid={`special-item-${it.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-zinc-950/50 px-4 py-3">
    {it.photo_version && (
      <button type="button" data-testid={`special-item-photo-${it.id}`} onClick={() => onZoom(it)} className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-white/10 transition-transform duration-200 hover:scale-105" aria-label={`View ${it.name} photo`}>
        <img src={itemPhotoUrl(sdId, it)} alt={it.name} className="h-full w-full object-cover" />
      </button>
    )}
    <div className="min-w-0 flex-1">
      <div className="font-semibold">{it.name}</div>
      <div className="text-xs text-zinc-500 mt-0.5"><span className="font-mono text-zinc-300">{money(it.price)}</span> each · <span data-testid={`special-item-left-${it.id}`} className={it.left ? "text-emerald-300" : "text-red-300"}>{it.left ? `${it.left} of ${it.quantity} left` : "Sold out"}</span></div>
    </div>
    {canClaim && it.left > 0 && <Stepper value={qty} max={it.left} onChange={setQty} testId={`special-qty-${it.id}`} />}
  </div>
);

const ClaimBox = ({ it, claims, isAdmin, onRemove }) => (
  <div data-testid={`special-claim-group-${it.id}`} className="rounded-2xl border border-white/5 bg-zinc-900/60 p-4">
    <div className="flex items-baseline justify-between gap-3 border-b border-white/5 pb-2.5 mb-2.5">
      <div className="min-w-0">
        <div className="font-semibold truncate">{it.name}</div>
        <div className="text-[11px] text-zinc-500">{it.type}</div>
      </div>
      <span data-testid={`special-claim-group-count-${it.id}`} className="font-mono text-sm font-bold text-amber-200 shrink-0">{it.claimed}/{it.quantity}</span>
    </div>
    <ul className="space-y-1">
      {claims.map((c) => (
        <li key={c.id} data-testid={`special-claim-${c.id}`} className="flex items-center gap-2 text-sm">
          <Link to={`/joiners/${c.joiner_id}`} className="flex-1 min-w-0 truncate hover:text-violet-300 transition-colors">{c.joiner_name}</Link>
          {c.qty > 1 && <span className="font-mono text-xs text-zinc-400">×{c.qty}</span>}
          {isAdmin && <button type="button" data-testid={`special-claim-remove-${c.id}`} onClick={() => onRemove(c)} aria-label="Remove claim" className="p-1 rounded-lg text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>}
        </li>
      ))}
    </ul>
  </div>
);

const ClaimsTable = ({ claims, items, isAdmin, onRemove }) => {
  const groups = items.map((it) => ({ it, claims: claims.filter((c) => c.item_id === it.id) })).filter((g) => g.claims.length);
  return (
    <div className="mt-10" data-testid="special-claims-list">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">Claims ({claims.length})</div>
      {claims.length === 0 ? <EmptyState title="No claims yet" hint="Be the first to claim an item." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((g) => <ClaimBox key={g.it.id} it={g.it} claims={g.claims} isAdmin={isAdmin} onRemove={onRemove} />)}
        </div>
      )}
    </div>
  );
};

export default function SpecialDropDetail() {
  const { id } = useParams();
  const [sd, setSd] = useState(null);
  const [joiners, setJoiners] = useState([]);
  const [joinerId, setJoinerId] = useState("");
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const isAdmin = !!getToken();
  const load = useCallback(() => {
    api.get(`/special-drops/${id}`).then((r) => {
      setSd(r.data);
      setQty((q) => Object.fromEntries(Object.entries(q).map(([k, n]) => [k, Math.min(n, r.data.items.find((i) => i.id === k)?.left || 0)])));
    }).catch(() => setSd(false));
  }, [id]);
  useEffect(() => { load(); api.get("/joiners").then((r) => setJoiners(r.data)).catch(() => {}); }, [load]);

  if (sd === null) return <div className="text-zinc-500 text-sm">Loading…</div>;
  if (sd === false) return <EmptyState title="Special drop not found" />;
  const canClaim = sd.status === "open" && !sd.claims_closed;
  const picked = Object.entries(qty).filter(([, n]) => n > 0);
  const total = picked.reduce((s, [iid, n]) => s + n * (sd.items.find((i) => i.id === iid)?.price || 0), 0);

  const submit = async () => {
    if (!joinerId || !picked.length) return toast.error("Choose your name and at least one item");
    setBusy(true);
    try {
      const r = await api.post(`/special-drops/${id}/claims`, { joiner_id: joinerId, items: picked.map(([item_id, n]) => ({ item_id, qty: n })) });
      toast.success(`Claimed: ${r.data.claimed.map((c) => `${c.qty}× ${c.item_name}`).join(", ")}`);
      setQty({});
      load();
    } catch (e) {
      toast.error(errMsg(e));
      load();
    } finally {
      setBusy(false);
    }
  };
  const removeClaim = async (c) => {
    if (!window.confirm(`Remove ${c.joiner_name}'s claim on ${c.qty}× ${c.item_name}?`)) return;
    try { await api.delete(`/special-drops/${id}/claims/${c.id}`); toast.success("Claim removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rise">
      <Link to="/drops" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> All drops</Link>
      <div className="flex flex-wrap items-center gap-2 mt-6">
        <StatusBadge testId="special-drop-status" status={sd.status === "open" && sd.claims_closed ? "closed" : sd.status} label={sd.status === "open" && sd.claims_closed ? "Claims closed" : undefined} />
        <span className="text-xs rounded-full bg-amber-500/15 text-amber-200 px-2 py-0.5 font-semibold">Special Drop</span>
      </div>
      <h1 data-testid="special-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">{sd.name}</h1>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-400" data-testid="special-meta">
        {sd.claims_close && <span>Closes <span className="text-zinc-200">{fmtKST(sd.claims_close)}</span></span>}
        {sd.payment_due && <span>Payment due <span className="text-zinc-200">{fmtKST(sd.payment_due)}</span></span>}
        <span>Send payments to <span className="text-zinc-200">{sd.pay_to}</span></span>
      </div>
      <p className="text-xs text-zinc-500 mt-2">First come, first served until each item runs out.</p>
      <StatusTimeline status={sd.status} />
      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          {ITEM_TYPES.filter((t) => sd.item_types.includes(t)).map((t) => (
            <section key={t} data-testid={`special-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="font-display text-base md:text-lg font-bold mb-2">{t}</h2>
              <div className="space-y-2">{sd.items.filter((i) => i.type === t).map((it) => <ItemRow key={it.id} it={it} sdId={sd.id} onZoom={setZoom} qty={qty[it.id] || 0} setQty={(n) => setQty({ ...qty, [it.id]: n })} canClaim={canClaim} />)}</div>
            </section>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-zinc-900/70 p-5 space-y-3 lg:sticky lg:top-24" data-testid="special-claim-panel">
          {canClaim ? (<>
            <div className="text-sm font-semibold">Claim items</div>
            <Select value={joinerId} onValueChange={setJoinerId}>
              <SelectTrigger data-testid="special-joiner-select" className="bg-zinc-950"><SelectValue placeholder="Select your name" /></SelectTrigger>
              <SelectContent>{joiners.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-between text-sm"><span className="text-zinc-400">{picked.reduce((s, [, n]) => s + n, 0)} items</span><span data-testid="special-claim-total" className="font-mono font-semibold">{money(total)}</span></div>
            <Button data-testid="special-claim-submit" disabled={busy} onClick={submit} className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-pink-600 text-white font-semibold">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Claim
            </Button>
          </>) : <div className="text-sm text-zinc-400" data-testid="special-claims-closed">Claims are closed for this special drop.</div>}
        </div>
      </div>
      <ClaimsTable claims={sd.claims} items={sd.items} isAdmin={isAdmin} onRemove={removeClaim} />
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 sm:max-w-2xl" data-testid="special-item-photo-dialog">
          <DialogTitle className="font-display">{zoom?.name}</DialogTitle>
          {zoom && <img src={itemPhotoUrl(sd.id, zoom)} alt={zoom.name} className="w-full max-h-[70vh] object-contain rounded-xl" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
